import classNames from 'classnames';
import { UI_MODE } from 'my-constants/UiMode';
import { cloneElement, useCallback, useMemo, useState } from 'react';

import { DARK_BLUE as DAT_DARK_BLUE, DAT_HIGHLIGHT_CONTROLS } from 'my-core/dat-quiz-ui-constants';
import {
  applyHighlightsToContent,
  hasContent,
  hasHighlights,
  IMAGE_BLOCK_CLASSNAME,
  intersectHighlights,
  mergeHighlights,
  toSolutionHighlights,
} from 'my-core/editor-utils';
import { DARK_BLUE, VERTICAL_SCROLLBAR_STYLE } from 'my-core/mcat-quiz-ui-constants';
import { hasSolution } from 'my-core/quiz-utils';

import MultiColumnContentsWrapper, {
  MULTI_COLUMN_DEFAULT_BREAKPOINT,
} from 'my-components/OnlineCourse/InCourseExperience/MultiColumnContentsWrapper';
import { LinkedOnlineCourseSections, SolutionContent } from 'my-components/QuizQuestion';
import QuizQuestionBanner from 'my-components/QuizQuestionBanner';
import QuizQuestionResult from 'my-components/QuizQuestionResult';
import Editor, { HighlightEditor, LoadingFallback, pluginPresets } from 'my-editor';
import { HIGHLIGHT_OVERLAP_COLOR } from 'my-editor/constants';
import MiniToggleButtonGroup from 'my-elements/MiniToggleButtonGroup';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

const rootCss = ({ breakpoints, constants, palette, shape: { borderRadius }, spacing }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: spacing(1),
  gap: spacing(1),

  // In a fixed-height parent the columns are capped at one view and scroll on their own; intro and
  // conclusion scroll into view around them rather than squeezing them.
  '.QL-columns': {
    padding: 0,
    flex: '0 0 auto',
    maxHeight: '100%',
    [breakpoints.down(MULTI_COLUMN_DEFAULT_BREAKPOINT)]: { maxHeight: 'unset' },
    [`@container (max-width: ${breakpoints.values[MULTI_COLUMN_DEFAULT_BREAKPOINT]}px)`]: { maxHeight: 'unset' },
  },
  '.QL-fullWidth': {
    flexShrink: 0,
    width: '100%',
    maxWidth: `calc(${constants.WRAPPER_MAX_WIDTH_MD}px * 2 + ${spacing(1)})`,
    alignSelf: 'center',
  },

  [`&.QL-root_${UI_MODE.MCAT}`]: {
    padding: 0,
    gap: 0,

    '.QL-columns': {
      flex: 1,
      gap: 0,
      '> div': {
        backgroundColor: palette.background.paper,
        '&:first-of-type': { borderRight: `6px solid ${DARK_BLUE}` },
        ...VERTICAL_SCROLLBAR_STYLE,
      },
    },

    '.QL-paper': { borderRadius: 0, border: 'none' },
  },
  [`&.QL-root_${UI_MODE.DAT}`]: {
    padding: 0,
    gap: 0,
    backgroundColor: palette.background.paper,

    '.QL-columns': { flex: 1, gap: 0 },
    '.QL-paper': { borderRadius: 0, border: 'none' },
    '&.QL-root_datPassage .QL-columns': {
      '> div': {
        flex: '1 1 0',
        overflow: 'auto',
        maxWidth: 'unset',
        '> div': { maxWidth: 850, margin: '0 auto' },
        ...VERTICAL_SCROLLBAR_STYLE,
        '&:first-of-type': { order: 99 },
        '+ div': { borderBottom: `1px solid ${DAT_DARK_BLUE}` },
      },
    },
  },
  '.QL-datPatContent': {
    [`.DraftEditor-root .${IMAGE_BLOCK_CLASSNAME}`]: {
      overflow: 'auto',
      paddingBottom: 10,
      '&::-webkit-scrollbar': { height: 5 },
      '&::-webkit-scrollbar-track': { backgroundColor: palette.paper2, borderRadius: 5 },
      '&::-webkit-scrollbar-thumb': { backgroundColor: palette.divider, borderRadius: 5 },
      img: { width: '100% !important', minWidth: '800px !important' },
    },
  },

  '.QL-paper': {
    backgroundColor: palette.background.paper,
    marginBottom: 0,
    border: `1px solid ${palette.divider}`,
    borderRadius,

    '+ .QL-paper': { marginTop: spacing(1) },
  },
  '.QL-padded': { padding: spacing(2) },

  '.QL-editor + .QL-editor': { marginTop: spacing(2) },

  '.QL-tabs': {
    margin: spacing(-2),
    marginBottom: spacing(2),
    borderTop: 'none',
  },
  '.QL-actions': { marginTop: spacing(4) },

  '.QL-header': {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',

    '+ *': { marginTop: spacing(2) },
  },
  '.QL-headerHint': {
    '+ *': { marginBottom: spacing(2) },
  },

  '.QL-questionStem': { marginBottom: spacing(2) },

  '.QL-highlightLayers': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing(1),
    marginBottom: spacing(1),
  },
  '.QL-highlightLegend': {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5),
    marginRight: 'auto',
  },
  '.QL-highlightLegendSwatch': {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: HIGHLIGHT_OVERLAP_COLOR,
  },

  [breakpoints.up('sm')]: {
    '.QL-padded': { padding: spacing(4) },
    '.QL-paddedHint': {
      paddingBottom: spacing(2),
      paddingTop: spacing(2),
    },
    '.QL-paper .QL-tabs': { margin: spacing(-4, -4, 4) },
  },
});

const HIGHLIGHT_LAYER = { MINE: 'mine', SOLUTION: 'solution' };
const HIGHLIGHT_LAYER_BUTTONS = [
  { label: 'My highlights', value: HIGHLIGHT_LAYER.MINE },
  { label: 'Solution', value: HIGHLIGHT_LAYER.SOLUTION },
];
const SOLUTION_ONLY_LAYER_BUTTONS = HIGHLIGHT_LAYER_BUTTONS.filter(({ value }) => value !== HIGHLIGHT_LAYER.MINE);
const DEFAULT_HIGHLIGHT_LAYERS = [HIGHLIGHT_LAYER.MINE, HIGHLIGHT_LAYER.SOLUTION];

export default function QuestionLayout(props) {
  const {
    actions: actionsProp,
    askAQuestion: askAQuestionProp,
    className,
    eventTrackingProperties,
    fetching,
    mpqPassageOnly,
    onUpdateSubmission,
    partIndex,
    position,
    question,
    questionComponent: questionComponentProp,
    questionType: questionTypeProp,
    sectionName,
    showResults,
    showSolution,
    submission,
    uiMode,
    video,
    weight,
  } = props;
  // Staff can open the solution without the question being graded, so this must never gate anything
  // that reveals the correct answer or the submission's score.
  const revealSolution = showResults || showSolution;

  const [showHint, setShowHint] = useState(false);
  const [visibleHighlightLayers, setVisibleHighlightLayers] = useState(DEFAULT_HIGHLIGHT_LAYERS);
  const isMCATMode = uiMode === UI_MODE.MCAT;
  const isDATMode = uiMode === UI_MODE.DAT;
  const questionType = question?.type || questionTypeProp;
  const isMPQ = questionType === 'MultiPartQuestion';

  const activeSubQuestion =
    isMPQ && partIndex != null && partIndex !== -1 ? question?.sub_questions?.[partIndex] : undefined;

  const authorHighlights = useMemo(
    () => mergeHighlights(question?.content_highlights, activeSubQuestion?.content_highlights),
    [question?.content_highlights, activeSubQuestion?.content_highlights],
  );
  const solutionHighlights = useMemo(
    () =>
      revealSolution ?
        mergeHighlights(question?.content_solution_highlights, activeSubQuestion?.content_solution_highlights)
      : undefined,
    [revealSolution, question?.content_solution_highlights, activeSubQuestion?.content_solution_highlights],
  );

  const myHighlights = submission?.highlights?.content;
  const hasMyHighlights = hasHighlights(myHighlights);
  const hasSolutionHighlights = hasHighlights(solutionHighlights);
  // The layer toggle only renders when the part has solution highlights, so a deselected layer must
  // be ignored otherwise (e.g. on another MPQ part) where there would be no control to restore it.
  const showMyHighlights = !hasSolutionHighlights || visibleHighlightLayers.includes(HIGHLIGHT_LAYER.MINE);
  const showSolutionHighlights = hasSolutionHighlights && visibleHighlightLayers.includes(HIGHLIGHT_LAYER.SOLUTION);

  const passageContent = useMemo(
    () =>
      applyHighlightsToContent(
        applyHighlightsToContent(question?.content, authorHighlights),
        showSolutionHighlights ? toSolutionHighlights(solutionHighlights) : undefined,
      ),
    [question?.content, authorHighlights, solutionHighlights, showSolutionHighlights],
  );

  // Only meaningful while both layers are on, and must be layered over them rather than under.
  const overlapHighlights = useMemo(
    () =>
      showMyHighlights && showSolutionHighlights ? intersectHighlights(myHighlights, solutionHighlights) : undefined,
    [myHighlights, solutionHighlights, showMyHighlights, showSolutionHighlights],
  );
  const hasOverlaps = !!overlapHighlights && Object.keys(overlapHighlights).length > 0;

  const updateHighlights = useCallback(
    (key, highlights) =>
      onUpdateSubmission({ ...submission, highlights: { ...submission?.highlights, [key]: highlights } }, question.id),
    [onUpdateSubmission, submission, question?.id],
  );

  const editorFallback = <LoadingFallback lines={10} />;

  const askAQuestion = askAQuestionProp && (
    <div className={classNames('QL-paper', 'QL-padded')}>{askAQuestionProp}</div>
  );

  const actions = actionsProp && <div className={classNames('QL-paper QL-padded')}>{actionsProp}</div>;

  const questionComponent =
    isMPQ ?
      cloneElement(questionComponentProp, {
        tabsClassName: 'QL-tabs',
      })
    : questionComponentProp;

  const hasSolutionContent = hasSolution(question);
  const solutionComponent = revealSolution && (video || hasSolutionContent) && (
    <>
      {video && (
        <div className="QL-paper" style={{ overflow: 'hidden' }}>
          {video}
        </div>
      )}
      {hasSolutionContent && (
        <div className={classNames('QL-paper', 'QL-padded')}>
          <div className="QL-header">
            <Typography variant="h6">Solution</Typography>
          </div>
          {/* <Editor plugins={pluginPresets.ALL} readOnly value={question?.solution} fallback={editorFallback} /> */}
          <SolutionContent solution={question.solution} />
          <LinkedOnlineCourseSections eventTrackingProperties={eventTrackingProperties} question={question} />
        </div>
      )}
    </>
  );

  // MCAT/DAT exam panes are fixed-height, so only the default layout lifts intro/conclusion out of the columns.
  const splitExtraContent = !uiMode;
  const conclusionComponent = revealSolution && hasContent(question?.conclusion_content) && (
    <div className={classNames('QL-paper', 'QL-padded', { 'QL-fullWidth': splitExtraContent })}>
      <div className="QL-header">
        <Typography variant="h6">Conclusion</Typography>
      </div>
      <Editor fallback={editorFallback} plugins={pluginPresets.ALL} readOnly value={question.conclusion_content} />
    </div>
  );

  const highlightLayersToggle = hasSolutionHighlights && (
    <div className="QL-highlightLayers">
      {hasOverlaps && (
        <Typography className="QL-highlightLegend" sx={{ color: 'text.secondary' }} variant="caption">
          <span className="QL-highlightLegendSwatch" />
          Highlighted by both you and the solution
        </Typography>
      )}
      <Typography sx={{ color: 'text.secondary' }} variant="caption">
        Show highlights
      </Typography>
      <MiniToggleButtonGroup
        buttons={hasMyHighlights ? HIGHLIGHT_LAYER_BUTTONS : SOLUTION_ONLY_LAYER_BUTTONS}
        exclusive={false}
        onChange={setVisibleHighlightLayers}
        value={visibleHighlightLayers}
      />
    </div>
  );

  const banner = <QuizQuestionBanner className="QL-paper" questionType={questionType} />;
  const header = !uiMode && (
    <>
      {sectionName && (
        <Typography sx={{ color: 'text.secondary' }} variant="subtitle2">
          {sectionName}
        </Typography>
      )}
      <div className="QL-header">
        <div>{!!position && <Typography variant="h6">{position})</Typography>}</div>
        <QuizQuestionResult
          partIndex={partIndex}
          question={question}
          showResults={showResults}
          submission={submission}
          weight={weight}
        />
      </div>
    </>
  );

  if (questionType === 'FillInTheBlank') {
    return (
      <div className={classNames('QL-root', className)} css={rootCss}>
        <MultiColumnContentsWrapper className="QL-columns">
          <div>
            <div className={classNames('QL-paper', 'QL-padded')} style={{ overflow: 'hidden' }}>
              {header}
              {questionComponent}
            </div>
            {renderHint()}
          </div>
          <div>
            {banner}
            {actions}
            {solutionComponent}
            {!splitExtraContent && conclusionComponent}
            {askAQuestion}
          </div>
        </MultiColumnContentsWrapper>
        {splitExtraContent && conclusionComponent}
      </div>
    );
  }

  const isDatPat =
    (question?.custom_tags?.dat?.section === 'pat' && question?.custom_tags.dat.subject !== 'cube_counting') ||
    question?.sub_questions?.some(
      sq => sq.custom_tags?.dat?.section === 'pat' && sq.custom_tags.dat.subject !== 'cube_counting',
    );
  const isDatRct =
    isDATMode &&
    (question?.custom_tags?.dat?.section === 'rct' ||
      (isMPQ && question?.sub_questions?.some(sq => sq.custom_tags?.dat?.section === 'rct')));

  const commonHighlightEditorProps = {
    className: 'QL-editor',
    clearHighlightOnLeftClick: isDATMode,
    controls: isDATMode ? DAT_HIGHLIGHT_CONTROLS : undefined,
    disableHighlighting: revealSolution,
    disableStrikeErase: isMCATMode,
    eventTrackingProperties: eventTrackingProperties,
    fallback: editorFallback,
    floatingControls: isDATMode,
    plugins: pluginPresets.ALL,
  };
  const introEditor = hasContent(question?.intro_content) && (
    <HighlightEditor
      {...commonHighlightEditorProps}
      highlights={submission?.highlights?.intro_content}
      onChangeHighlights={highlights => updateHighlights('intro_content', highlights)}
      value={question.intro_content}
    />
  );
  const introPaper = splitExtraContent && introEditor && (
    <div className={classNames('QL-paper', 'QL-padded', 'QL-fullWidth')}>
      {header}
      {introEditor}
    </div>
  );
  return (
    <div
      className={classNames('QL-root', className, {
        [`QL-root_${uiMode}`]: uiMode,
        'QL-root_datPassage': isDatRct,
      })}
      css={rootCss}
    >
      {introPaper}
      <MultiColumnContentsWrapper
        className="QL-columns"
        minColumnWidth={isDatPat ? 870 : undefined}
        variant={
          isMCATMode ? 'multiColumn'
          : isDATMode ?
            'singleColumn'
          : 'default'
        }
      >
        <div>
          {/* don't apply overflow hidden as will mess up highlight toolbar fixed positioning */}
          <div className={classNames('QL-paper', 'QL-padded')}>
            {!introPaper && header}
            {highlightLayersToggle}
            {!splitExtraContent && introEditor}
            {question?.content ?
              <HighlightEditor
                {...commonHighlightEditorProps}
                className={classNames(commonHighlightEditorProps.className, { 'QL-datPatContent': isDatPat })}
                highlights={showMyHighlights ? myHighlights : undefined}
                onChangeHighlights={highlights => updateHighlights('content', highlights)}
                overlayHighlights={overlapHighlights}
                value={passageContent}
              />
            : fetching && editorFallback}
          </div>
          {renderHint()}
        </div>
        {!mpqPassageOnly && (
          <div>
            {banner}
            <div className={classNames('QL-paper', 'QL-padded')} style={{ overflow: 'hidden' }}>
              {hasContent(question?.stem_content) && (
                <HighlightEditor
                  {...commonHighlightEditorProps}
                  className={classNames(commonHighlightEditorProps.className, 'QL-questionStem')}
                  highlights={submission?.highlights?.stem_content}
                  onChangeHighlights={highlights => updateHighlights('stem_content', highlights)}
                  value={question.stem_content}
                />
              )}
              {questionComponent}
            </div>
            {actions}
            {solutionComponent}
            {!splitExtraContent && conclusionComponent}
            {askAQuestion}
          </div>
        )}
      </MultiColumnContentsWrapper>
      {splitExtraContent && conclusionComponent}
    </div>
  );

  function renderHint() {
    if (hasContent(question?.hint)) {
      return (
        <div className={classNames('QL-paper', 'QL-padded', 'QL-paddedHint')}>
          <div className={classNames('QL-header', 'QL-headerHint')}>
            <Typography variant="h6">Hint</Typography>
            <Button onClick={() => setShowHint(!showHint)} size="small" variant="outlined">
              {`${showHint ? 'Hide' : 'Show'} Hint`}
            </Button>
          </div>
          {showHint && <Editor fallback={editorFallback} plugins={pluginPresets.ALL} readOnly value={question?.hint} />}
        </div>
      );
    }
  }
}

// QuestionLayout.propTypes = {
//   actions: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
//   askAQuestion: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
//   position: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
//   question: PropTypes.object,
//   questionComponent: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
//   showResults: PropTypes.bool,
//   submission: PropTypes.object,
//   video: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
//   weight: PropTypes.number,
// };
